import type { Instance } from '@popperjs/core'
import { createPopper } from '@popperjs/core'
import clsx from 'clsx'
import { endOfWeek, startOfWeek } from 'date-fns'
import type { KeyboardEventHandler, Ref } from 'react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import type { ChangeEvent } from '../DatePicker/DatePicker'
import DatePicker, { Modes } from '../DatePicker/DatePicker'
import useDateFormatters from '../hooks/useDateFormatters'
import { useSyncRefs } from '../hooks/useSyncRefs'
import DateRange from '../icons/DateRange'
import EventIcon from '../icons/Event'
import ExpandMore from '../icons/ExpandMore'
import Portal from '../Portal/Portal'
import type { TextInputSize, TextInputVariant } from '../TextInput/styles'
import TextInput from '../TextInput/TextInput'
import getCurrentLocale from '../utilities/getCurrentLocale'
import getStartOfWeek from '../utilities/getStartOfWeek'
import { Keys } from '../utilities/keyboard'

type BaseDatePickerInputProps = {
  mode?: Modes
}

type DatePickerInputProps = {
  clearRef?: Ref<HTMLButtonElement>
  condensed?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  initialDate?: [Date, Date] | Date | null
  inputRef?: Ref<HTMLInputElement>
  label: string
  name?: string
  onChange?: (event: ChangeEvent) => void
  size?: TextInputSize
  variant?: TextInputVariant
}

export type DayPickerInputProps = {
  onChange?: (event: SingleDateChangeEvent) => void
} & Omit<DatePickerInputProps, 'onChange'>

type MultiDateChangeEvent = {
  target: {
    value: Date[]
  }
}

type RangePickerInputProps = {
  onChange?: (event: MultiDateChangeEvent) => void
} & Omit<DatePickerInputProps, 'onChange'>

type SingleDateChangeEvent = {
  target: {
    value: Date
  }
}

type WeekPickerInputProps = {
  onChange?: (event: MultiDateChangeEvent) => void
} & Omit<DatePickerInputProps, 'onChange'>

function DatePickerInput({
  clearRef,
  condensed = false,
  disabled = false,
  error = false,
  helperText,
  initialDate = new Date(),
  inputRef = null,
  label,
  mode = Modes.Day,
  name,
  onChange,
  size = 'medium',
  variant = 'outlined',
}: BaseDatePickerInputProps & DatePickerInputProps): JSX.Element {
  const intl = useIntl()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const instanceRef = useRef<Instance | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)
  const localRef = useRef<HTMLInputElement | null>(null)
  const inputValueRef = useRef<string>('')
  const syncRef = useSyncRefs(localRef, inputRef)
  const [open, setOpen] = useState(false)

  const { formatDateLocal } = useDateFormatters()

  useEffect(() => {
    function clickAway(event: MouseEvent) {
      const target = event.target as HTMLElement

      if (!open) {
        return
      }

      if (popperRef.current?.contains(target)) {
        return
      }

      if (containerRef.current?.contains(target)) {
        return
      }

      setOpen(false)
    }

    window.addEventListener('mousedown', clickAway)

    return () => window.removeEventListener('mousedown', clickAway)
  }, [open])

  useLayoutEffect(() => {
    if (open) {
      instanceRef.current = createPopper(
        containerRef.current as HTMLDivElement,
        popperRef.current as HTMLDivElement,
        {
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom'],
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
          placement: 'bottom-start',
        },
      )

      popperRef.current?.querySelector('button')?.focus()
    } else {
      instanceRef.current?.destroy()

      instanceRef.current = null
    }
  }, [open])

  const formatValue = useCallback(
    (dates: (Date | null)[]): string => {
      if (!dates[0]) {
        return ''
      }

      if (mode === Modes.Range) {
        if (!dates[0] || !dates[1]) {
          return ''
        }

        return formatDateLocal(dates[0]) + ' — ' + formatDateLocal(dates[1])
      }

      if (mode === Modes.Week) {
        const locale = getCurrentLocale(intl.locale)
        const weekStartsOn = getStartOfWeek(intl.locale)

        const start = startOfWeek(dates[0], { locale, weekStartsOn })
        const end = endOfWeek(dates[0], { locale, weekStartsOn })

        return formatDateLocal(start) + ' — ' + formatDateLocal(end)
      }

      return formatDateLocal(dates[0])
    },
    [formatDateLocal, intl.locale, mode],
  )

  const handleChange = (event: ChangeEvent) => {
    if (localRef.current) {
      let value: string

      if (mode === Modes.Range) {
        const [start, end] = event.target.value as Date[]

        value = formatValue([start, end])
      } else {
        value = formatValue([event.target.value as Date])
      }

      // Do not close the picker when selecting the first date in the range
      if (
        localRef.current.value !== value &&
        !(mode === Modes.Range && !value)
      ) {
        setOpen(false)
      }

      inputValueRef.current = value

      const descriptor = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      ) as PropertyDescriptor
      const inputEvent = new Event('input', { bubbles: true })

      descriptor.set?.call(localRef.current, value)
      localRef.current.dispatchEvent(inputEvent)
    }

    if (mode === Modes.Week) {
      const date = event.target.value as Date

      const locale = getCurrentLocale(intl.locale)
      const weekStartsOn = getStartOfWeek(intl.locale)

      const start = startOfWeek(date, { locale, weekStartsOn })
      const end = endOfWeek(start, { locale, weekStartsOn })

      event.target.value = [start, end]
    }

    onChange?.(event)
  }

  const handleFocus = () => {
    setOpen(true)
    // fix the label going up and down when input is empty
    localRef.current?.blur()
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === Keys.Escape) {
      setOpen(false)
    }
  }

  const defaultValue = useMemo(() => {
    if (mode === Modes.Range) {
      return formatValue(Array.isArray(initialDate) ? initialDate : [null])
    }

    return formatValue(
      Array.isArray(initialDate) ? [initialDate[0]] : [initialDate],
    )
  }, [formatValue, initialDate, mode])

  return (
    <div
      /*
       * We can safely ignore the `no-static-element-interactions` warning here
       * because our overlay is only designed to capture any outside
       * clicks, not serve as a clickable element itself.
       *
       * https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md#case-the-event-handler-is-only-being-used-to-capture-bubbled-events
       */
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div ref={containerRef}>
        <TextInput
          appearFocused={open}
          defaultValue={defaultValue}
          disabled={disabled}
          endAdornment={ExpandMore}
          error={error}
          helperText={helperText}
          inputRef={syncRef}
          label={label}
          name={name}
          onFocus={handleFocus}
          readOnly
          selectable
          size={size}
          startAdornment={mode === Modes.Day ? EventIcon : DateRange}
          variant={variant}
        />
      </div>
      <Portal open={open}>
        <div className={clsx('z-9998', open ? '' : 'hidden')} ref={popperRef}>
          <DatePicker
            clearRef={clearRef}
            condensed={condensed}
            initialDate={initialDate}
            inputRef={localRef}
            inputValueRef={inputValueRef}
            mode={mode}
            onChange={handleChange}
            open={open}
          />
        </div>
      </Portal>
    </div>
  )
}

function DayPickerInput({
  clearRef = null,
  condensed = false,
  disabled = false,
  error = false,
  helperText,
  initialDate = new Date(),
  inputRef,
  label,
  name,
  onChange,
  size = 'medium',
  variant = 'outlined',
}: DayPickerInputProps): JSX.Element {
  return (
    <DatePickerInput
      clearRef={clearRef}
      condensed={condensed}
      disabled={disabled}
      error={error}
      helperText={helperText}
      initialDate={initialDate}
      inputRef={inputRef}
      label={label}
      mode={Modes.Day}
      name={name}
      onChange={onChange}
      size={size}
      variant={variant}
    />
  )
}

function RangePickerInput({
  clearRef = null,
  condensed = false,
  disabled = false,
  error = false,
  helperText,
  initialDate = new Date(),
  inputRef,
  label,
  name,
  onChange,
  size = 'medium',
  variant = 'outlined',
}: RangePickerInputProps): JSX.Element {
  return (
    <DatePickerInput
      clearRef={clearRef}
      condensed={condensed}
      disabled={disabled}
      error={error}
      helperText={helperText}
      initialDate={initialDate}
      inputRef={inputRef}
      label={label}
      mode={Modes.Range}
      name={name}
      onChange={onChange}
      size={size}
      variant={variant}
    />
  )
}

function WeekPickerInput({
  clearRef = null,
  condensed = false,
  disabled = false,
  error = false,
  helperText,
  initialDate = new Date(),
  inputRef,
  label,
  name,
  onChange,
  size = 'medium',
  variant = 'outlined',
}: WeekPickerInputProps): JSX.Element {
  return (
    <DatePickerInput
      clearRef={clearRef}
      condensed={condensed}
      disabled={disabled}
      error={error}
      helperText={helperText}
      initialDate={initialDate}
      inputRef={inputRef}
      label={label}
      mode={Modes.Week}
      name={name}
      onChange={onChange}
      size={size}
      variant={variant}
    />
  )
}

export { DayPickerInput, RangePickerInput, WeekPickerInput }
